import z from 'zod';

import { createElement } from '@@plate/serialization/creators/createElement';
import { getElementTypes } from '@@plate/utils/getElementTypes';

import { BlueskyCodec, PlateBluesky, UnityBluesky } from './Bluesky';
import {
    CrossheadCodec,
    PlateCrossheadDefault,
    PlateCrossheadOrderedListicle,
    PlateCrossheadSubsection,
    PlateCrossheadUnorderedListicle,
    UnityCrosshead,
} from './Crosshead';
import {
    EmbeddedContentCodec,
    PlateEmbeddedContent,
    UnityEmbeddedContent,
} from './EmbeddedContent';
import { FacebookCodec, PlateFacebook, UnityFacebook } from './Facebook';
import { FooterCodec, PlateFooter, UnityFooter } from './Footer';
import { ImageCodec, PlateImage, UnityImage } from './Image';
import { InstagramCodec, PlateInstagram, UnityInstagram } from './Instagram';
import {
    OrderedListCodec,
    PlateOrderedList,
    PlateUnorderedList,
    UnorderedListCodec,
    UnityOrderedList,
    UnityUnorderedList,
} from './List';
import { ParagraphCodec, PlateParagraph, UnityParagraph } from './Paragraph';
import { PlateThreads, ThreadsCodec, UnityThreads } from './Threads';
import { PlateTiktok, TiktokCodec, UnityTiktok } from './Tiktok';
import { UnityTitle } from './Title';
import { PlateTwitter, TwitterCodec, UnityTwitter } from './Twitter';
import { PlateYoutube, UnityYoutube, YoutubeCodec } from './Youtube';
import { PlateZattoo, UnityZattoo, ZattooCodec } from './Zattoo';
import { CURRENT_VERSION, ElementId, UnityElementId, Version } from '../commons';
import { ELEMENT_TYPES, UNITY_ELEMENT_TYPES } from '../constants';

const ContainerType = z.enum(['_infobox_default', '_infobox_important', '_infobox_summary']);
type ContainerType = z.infer<typeof ContainerType>;

/**
 * * This comment is for translation auto extract
 * t('embed.infobox.default')
 * t('embed.infobox.important')
 * t('embed.infobox.summary')
 */
export const CONTAINER_TYPE_MAPPINGS = {
    _infobox_default: 'embed.infobox.default',
    _infobox_important: 'embed.infobox.important',
    _infobox_summary: 'embed.infobox.summary',
};

const UnityInfoboxContentElement = z.discriminatedUnion(
    'type',
    [
        UnityCrosshead,
        UnityFacebook,
        UnityFooter,
        UnityImage,
        UnityInstagram,
        UnityOrderedList,
        UnityParagraph,
        UnityThreads,
        UnityTwitter,
        UnityUnorderedList,
        UnityBluesky,
        UnityYoutube,
        UnityTiktok,
        UnityZattoo,
        UnityEmbeddedContent,
    ],
    { unionFallback: true },
);
type UnityInfoboxContentElement = z.infer<typeof UnityInfoboxContentElement>;

export const PlateInfoboxContentElement = z.discriminatedUnion(
    'type',
    [
        PlateCrossheadDefault,
        PlateCrossheadSubsection,
        PlateCrossheadOrderedListicle,
        PlateCrossheadUnorderedListicle,
        PlateFacebook,
        PlateFooter,
        PlateImage,
        PlateInstagram,
        PlateOrderedList,
        PlateParagraph,
        PlateThreads,
        PlateTwitter,
        PlateUnorderedList,
        PlateBluesky,
        PlateYoutube,
        PlateTiktok,
        PlateZattoo,
        PlateEmbeddedContent,
    ],
    { unionFallback: true },
);
export type PlateInfoboxContentElement = z.infer<typeof PlateInfoboxContentElement>;

const InfoboxElementsCodec = z.discriminatedUnion(
    'type',
    [
        CrossheadCodec,
        FacebookCodec,
        FooterCodec,
        ImageCodec,
        InstagramCodec,
        OrderedListCodec,
        ParagraphCodec,
        ThreadsCodec,
        TwitterCodec,
        UnorderedListCodec,
        BlueskyCodec,
        YoutubeCodec,
        TiktokCodec,
        ZattooCodec,
        EmbeddedContentCodec,
    ],
    { unionFallback: true },
);

export const PlateInfoboxTitle = z.object({
    ...PlateParagraph.shape,
    type: z.literal(ELEMENT_TYPES.INFOBOX_TITLE),
});
export type InfoboxTitleElement = z.infer<typeof PlateInfoboxTitle>;

export const PlateInfoboxContent = z.object({
    type: z.literal(ELEMENT_TYPES.INFOBOX_CONTENT),
    data: z
        .object({
            templateElement: z.boolean().optional(),
        })
        .optional(),
    children: z.array(PlateInfoboxContentElement),
});
export type InfoboxContentElement = z.infer<typeof PlateInfoboxContent>;

export const INFOBOX_CONTENT_ELEMENT_TYPES = getElementTypes([PlateInfoboxContent]);

export const UnityInfobox = z.object({
    type: z.literal(ELEMENT_TYPES.INFOBOX),
    elementId: UnityElementId,
    templateElement: z.boolean().optional(),
    version: Version,
    title: z.union([UnityParagraph, UnityTitle]).nullable(),
    content: z.array(UnityInfoboxContentElement).nullable(),
    collapsed: z.boolean().optional(),
    style: z
        .object({
            containerType: ContainerType.optional(),
        })
        .nullable(),
});
export type UnityInfobox = z.infer<typeof UnityInfobox>;

export const PlateInfobox = z.object({
    type: z.literal(ELEMENT_TYPES.INFOBOX),
    id: ElementId,
    data: z.object({
        templateElement: z.boolean().optional(),
        collapsed: z.boolean().optional(),
        style: z
            .object({
                containerType: ContainerType,
            })
            .nullable(),
    }),
    children: z.tuple([PlateInfoboxTitle, PlateInfoboxContent]),
});
export type InfoboxElement = z.infer<typeof PlateInfobox>;

export const INFOBOX_ELEMENT_TYPES = getElementTypes([PlateInfobox]);

export const InfoboxCodec = z.codec(UnityInfobox, PlateInfobox, {
    decode: (value) => {
        const title: InfoboxTitleElement = value.title
            ? {
                  ...ParagraphCodec.decode({ ...value.title, type: UNITY_ELEMENT_TYPES.TEXT }),
                  type: ELEMENT_TYPES.INFOBOX_TITLE,
              }
            : createElement(ELEMENT_TYPES.INFOBOX_TITLE, {
                  children: [{ text: '' }],
              });

        const content: InfoboxContentElement = {
            type: ELEMENT_TYPES.INFOBOX_CONTENT,
            children: value.content
                ? value.content.map((contentItem) => InfoboxElementsCodec.decode(contentItem))
                : [],
        };

        return {
            type: value.type,
            id: value.elementId ?? crypto.randomUUID(),
            data: {
                collapsed: value.collapsed,
                style: value.style
                    ? { containerType: value.style.containerType ?? '_infobox_default' }
                    : null,
                ...(value.templateElement && { templateElement: value.templateElement }),
            },
            children: [title, content] satisfies [InfoboxTitleElement, InfoboxContentElement],
        };
    },
    encode: (value) => ({
        type: value.type,
        elementId: value.id,
        version: CURRENT_VERSION,
        title: ParagraphCodec.encode({
            ...value.children[0],
            type: ELEMENT_TYPES.PARAGRAPH,
        }),
        content: value.children[1].children.map((child) => InfoboxElementsCodec.encode(child)),
        ...value.data,
    }),
});
